import React, { JSX, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';
import { useAuth } from 'hooks/AuthContext';
import { parseAuthErrorMessage } from './emailVerificationError';

export const MFAVerificationDialog = (props: {
  open: boolean;
  onClose: () => void;
  onVerificationSuccess: () => void;
}): JSX.Element => {
  const { open, onClose, onVerificationSuccess } = props;
  const { verifyMFA, resendMFACode, clearMFA, mfaResponse, isLoading, error } = useAuth();
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [localError, setLocalError] = useState('');
  const [timeLeft, setTimeLeft] = useState(600);
  const errorMessage = parseAuthErrorMessage(error);

  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setLocalError('MFA code has expired. Please try logging in again.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [open]);

  const handleVerifyMFA = async (): Promise<void> => {
    if (!code || code.length !== 6) {
      setLocalError('Please enter a valid 6-digit code');
      return;
    }
    if (!mfaResponse?.mfaData) {
      setLocalError('MFA data not available');
      return;
    }
    setLocalError('');
    try {
      const success = await verifyMFA({
        email: mfaResponse.mfaData.email,
        code,
        deviceId: mfaResponse.mfaData.deviceId,
        rememberDevice,
      });
      if (success) {
        onVerificationSuccess();
        onClose();
      }
    } catch {
      setLocalError('Verification failed. Please try again.');
    }
  };

  const handleResendCode = async (): Promise<void> => {
    if (!mfaResponse?.mfaData) {
      setLocalError('MFA data not available');
      return;
    }
    try {
      const success = await resendMFACode(
        mfaResponse.mfaData.email,
        mfaResponse.mfaData.deviceId,
        mfaResponse.mfaData.deviceName
      );
      if (success) {
        setTimeLeft(600);
        setLocalError('');
        alert('New verification code sent to your email');
      }
    } catch {
      setLocalError('Failed to resend code');
    }
  };

  const handleClose = (): void => {
    clearMFA();
    onClose();
  };

  if (!mfaResponse?.mfaData) return <></>;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SecurityIcon color="primary" />
          <Typography variant="h6">Verify Your Identity</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          We&apos;ve detected a login from a new device:{' '}
          <strong>{mfaResponse.mfaData.deviceName}</strong>
        </Alert>
        <Typography variant="body1" gutterBottom>
          We&apos;ve sent a 6-digit verification code to:
        </Typography>
        <Typography variant="h6" color="primary" gutterBottom>
          {mfaResponse.mfaData.email}
        </Typography>
        <TextField
          fullWidth
          label="Enter 6-digit code"
          value={code}
          onChange={e => {
            setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
            setLocalError('');
          }}
          placeholder="000000"
          inputProps={{
            maxLength: 6,
            style: { fontSize: 24, textAlign: 'center', letterSpacing: 8 },
          }}
          sx={{ mt: 2, mb: 2 }}
          error={!!(localError || errorMessage)}
          helperText={localError || errorMessage}
        />
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="body2" color="text.secondary">
            Code expires in: {formatTime(timeLeft)}
          </Typography>
          <Button size="small" onClick={handleResendCode} disabled={isLoading || timeLeft > 540}>
            Resend Code
          </Button>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={rememberDevice}
              onChange={e => setRememberDevice(e.target.checked)}
            />
          }
          label="Remember this device for 90 days"
        />
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            If you didn&apos;t attempt to log in, please change your password immediately.
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleVerifyMFA}
          disabled={isLoading || !code || code.length !== 6 || timeLeft === 0}
        >
          {isLoading ? <CircularProgress size={20} /> : 'Verify'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
